/* assets/of-bench-core.js (added 2026-08-29, workbench cart drawer P1-3)
 *
 * Pure deal-ladder math for "your bench". No DOM, no network, no side
 * effects at load. Dual exported (window.OFBenchCore in the browser,
 * module.exports in node) exactly like of-brands-core.js and
 * of-help-index-core.js, so every decision here is covered by
 * tests/bench-core.test.mjs.
 *
 * CONSUMERS:
 *   - assets/of-bench-widget.js, the header workbench icon and its gauge.
 *   - assets/of-cart-drawer.js, the drawer's deal ladder.
 * Both read ONE computation, so the header and the drawer cannot disagree
 * about a tier. Before this file existed, every function below was private
 * inside the widget's IIFE and the drawer would have had to copy it.
 *
 * WHICH MATCHER WINS, AND WHY.
 * There were two tag matchers against one rule set. __OFNudgesCore.matches
 * returns TRUE for a rule whose any[] and all[] are both empty, so such a
 * rule matches every product in the cart. of-bench-widget.js used a
 * stricter copy that also required at least one of the two arrays to be
 * non-empty. The strict one wins here and is the only one this file has.
 * A tier rule with no tag filter would otherwise sweep the whole cart into
 * a single pool and paint a lane claiming a discount the checkout does not
 * give, which is worse than showing nothing. The nudge core keeps its
 * looser matcher for per-line pills, where an unfiltered rule is a
 * merchant's deliberate storewide badge and the pill is decoration rather
 * than a promise about a quantity pool.
 *
 * THE GOLD PASS IS GONE. The widget used to loop every rule against every
 * cart item twice: once to build the rows (skipping finished ladders) and
 * again purely to decide whether any pool sat at its top tier. Finished
 * ladders now keep their lane (P1-4), so one pass answers both questions.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.OFBenchCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  /* the color language: gold means a pool reached its best price */
  var GOLD = '#c08a2e'; /* amber gold: b08d2e read olive-green at glyph size (Rhett 8/29) */
  var DEFAULT_FG = '#991b1b';
  var DEFAULT_BG = '#fee2e2';

  function norm(arr) {
    var out = [];
    if (!arr || !arr.length) return out;
    for (var i = 0; i < arr.length; i++) {
      var t = String(arr[i] == null ? '' : arr[i]).toLowerCase().trim();
      if (t) out.push(t);
    }
    return out;
  }

  /* Product tags arrive lowercased from /products/<handle>.js in the
     widget and from data attributes in the drawer. Normalize anyway so a
     caller that forgot cannot produce a silent miss. */
  function normalizeTags(tags) {
    return norm(tags);
  }

  /* The strict matcher. See the header comment for why this one wins. */
  function matches(tags, rule) {
    if (!rule) return false;
    var any = norm(rule.any);
    var all = norm(rule.all);
    if (!any.length && !all.length) return false;
    var i;
    if (any.length) {
      var hit = false;
      for (i = 0; i < any.length; i++) {
        if (tags.indexOf(any[i]) !== -1) { hit = true; break; }
      }
      if (!hit) return false;
    }
    for (i = 0; i < all.length; i++) {
      if (tags.indexOf(all[i]) !== -1) continue;
      return false;
    }
    return true;
  }

  /* Merchant-edited colours are about to be interpolated into a style
     attribute on two surfaces. Anything that is not plainly a colour falls
     back to the default oxblood rather than reaching the DOM. Lives here,
     not in the widget, because of-cart-drawer.js interpolates the same
     field into the same attribute and had no validation at all. */
  function safeColor(c) {
    var s = String(c == null ? '' : c).trim();
    return /^#[0-9a-f]{3,8}$/i.test(s) || /^[a-z]{3,20}$/i.test(s) ? s : DEFAULT_FG;
  }

  /* THE LANE PALETTE.
   *
   * Every badge rule in the shop carries the same khaki in its
   * text_color, because that field was authored for the collection card
   * chip, where one house colour is the point. Read as the lane colour it
   * made two deals in one cart look like one deal in two rows (Rhett
   * 9/2, on the header pop-up). Colour on a lane has a job: it says WHICH
   * deal, and length says how far.
   *
   * Deep, muted, saturated. Rhett's brief: "Apple Pro line colours
   * instead of regular line colours", nothing pastel and nothing candy.
   * Every one of these clears 4.5:1 against the pop-up's white and the
   * drawer's #fdfcf9, so the same value can tint the lane's name and
   * status text as well as fill its bar.
   *
   * Assignment is a NAMED MAP, slug to colour, not a hash over the
   * palette. A hash is stable, which is the property that matters, but it
   * is not injective: run today's ten rules through one and beading tools,
   * findings and the free shipping flag all land on midnight, which
   * recreates the exact problem in a new colour. The six ladder rules are
   * a short, slow-moving list, so they are spelled out. A rule that is not
   * in the map still gets a colour, hashed over the entries the map has
   * not spent, so a new rule in admin paints something distinct without a
   * deploy.
   *
   * Deliberately here and not in a stylesheet keyed by class: the header
   * rail, the pop-down and the drawer all read laneFor(), so one mapping
   * means one colour per deal on all three and a lane stays recognisable
   * as the customer moves between them.
   *
   * Gold is not in the set. It is reserved for a pool that reached its top
   * tier, and a lane wearing it by luck would say "secured" before it was.
   */
  var LANE_PALETTE = [
    '#3a3f47', /* graphite */
    '#1f2d4a', /* midnight */
    '#2f4a3a', /* forest */
    '#6b2a2a', /* oxblood */
    '#8a6a2f', /* bronze */
    '#4a2f4f', /* plum */
    '#3f5670', /* slate blue */
    '#245150'  /* deep teal */
  ];

  /* The live ladder rules, 2026-09-02. Where a colour has an obvious
     reading it takes it: steel for saw blades and pliers, bronze for the
     supplies and gravers pool, oxblood for burs. */
  var LANE_COLORS = {
    'supplies-bulk-discount': '#8a6a2f',      /* bronze */
    'burs-bulk-discount': '#6b2a2a',          /* oxblood */
    'tools-bulk-discount': '#3a3f47',         /* graphite */
    'beading-tools-bulk-discount': '#245150', /* deep teal */
    'findings-bulk-discount': '#1f2d4a',      /* midnight */
    'files-bulk-discount': '#3f5670'          /* slate blue */
  };

  var LANE_SPARE = (function () {
    var taken = [];
    for (var k in LANE_COLORS) {
      if (Object.prototype.hasOwnProperty.call(LANE_COLORS, k)) taken.push(LANE_COLORS[k]);
    }
    var out = [];
    for (var i = 0; i < LANE_PALETTE.length; i++) {
      if (taken.indexOf(LANE_PALETTE[i]) === -1) out.push(LANE_PALETTE[i]);
    }
    return out.length ? out : LANE_PALETTE;
  })();

  function laneColor(rule) {
    var slug = String((rule && rule.slug) || '').trim();
    /* No slug is no stable key, so nothing can be assigned: fall back to
       the merchant's own field through the validation it always had. */
    if (!slug) return safeColor(rule && rule.text_color);
    if (Object.prototype.hasOwnProperty.call(LANE_COLORS, slug)) return LANE_COLORS[slug];
    var h = 0;
    for (var i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
    return LANE_SPARE[h % LANE_SPARE.length];
  }

  /* THE ONE TIER PARSE for every bench surface. Tiers ascending, coerced to
     numbers, junk dropped. A rule whose tiers survive this as an empty
     array has no ladder and never gets a lane.
     Reconciled against __OFNudgesCore.normTiers, the other parse of the
     same metaobject field, and this is the stricter of the two on both
     axes it differs on. normTiers strips non-digits, so "10+" becomes a
     tier at 10; here it is NaN and gets dropped, because a ladder drawn
     from a quantity nobody typed is a promise the checkout will not keep.
     And a tier at 0% is no tier at all: it would paint a lane and a rail
     segment for a discount of nothing, so like normTiers it goes. The
     nudge core keeps its own copy for the per-line pills; nothing on the
     bench calls it. */
  function ascTiers(rule) {
    var raw = (rule && rule.tiers) || [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var t = raw[i];
      if (!t) continue;
      var qty = Number(t.min_qty);
      var pct = Number(t.percent);
      if (!isFinite(qty) || qty <= 0) continue;
      if (!isFinite(pct) || pct <= 0) continue;
      out.push({ min_qty: qty, percent: pct });
    }
    out.sort(function (a, b) { return a.min_qty - b.min_qty; });
    return out;
  }

  /* Rail percent with EQUAL SEGMENTS PER TIER (the equal-thirds contract
     Rhett set for the header rail, generalized to n tiers). Each tier owns
     1/n of the track no matter how far apart the quantities are, so a
     ladder of 3/6/24 does not spend 75% of its rail on the last step. */
  function railPct(qty, asc) {
    if (!asc || !asc.length) return 0;
    if (qty <= 0) return 0;
    var lo = 0;
    for (var i = 0; i < asc.length; i++) {
      if (qty < asc[i].min_qty) {
        return ((i + (qty - lo) / (asc[i].min_qty - lo)) * 100) / asc.length;
      }
      lo = asc[i].min_qty;
    }
    return 100;
  }

  /* Tick positions (in percent) for every tier boundary except the last,
     which is the end of the track. */
  function tickPercents(asc) {
    var out = [];
    if (!asc || asc.length < 2) return out;
    for (var i = 0; i < asc.length - 1; i++) {
      out.push(((i + 1) * 100) / asc.length);
    }
    return out;
  }

  /* Label positions: 0 sits at the left edge, every tier label sits at its
     own boundary, and the top tier's label sits at the right edge. */
  function labelPercents(asc) {
    var out = [];
    if (!asc || !asc.length) return out;
    for (var i = 0; i < asc.length; i++) {
      out.push(((i + 1) * 100) / asc.length);
    }
    return out;
  }

  function laneFor(rule, qty) {
    var asc = ascTiers(rule);
    if (!asc.length) return null;
    var met = 0;
    for (var i = 0; i < asc.length; i++) {
      if (qty >= asc[i].min_qty) met = i + 1;
    }
    var next = null;
    for (var j = 0; j < asc.length; j++) {
      if (asc[j].min_qty > qty) { next = asc[j]; break; }
    }
    var complete = met === asc.length;
    return {
      slug: String((rule && rule.slug) || ''),
      label: String((rule && (rule.cart_label || rule.label)) || (rule && rule.slug) || ''),
      color: laneColor(rule),
      bg: (rule && rule.bg_color && String(rule.bg_color).trim()) || DEFAULT_BG,
      linkUrl: (rule && typeof rule.link_url === 'string' && rule.link_url) || '/pages/buy-more-save-more',
      qty: qty,
      tiers: asc,
      tierIndex: met,
      tierCount: asc.length,
      currentPercent: met > 0 ? asc[met - 1].percent : 0,
      nextPercent: next ? next.percent : null,
      nextQty: next ? next.min_qty : null,
      toNext: next ? next.min_qty - qty : 0,
      pct: railPct(qty, asc),
      complete: complete,
      near: !!next && next.min_qty - qty === 1
    };
  }

  /* ONE pass over rules x cart lines.
   *
   * @param cartItems   array of { handle, quantity } (a /cart.js items
   *                    array works as is; extra keys are ignored)
   * @param tagsByHandle map handle -> array of product tags. A handle with
   *                    no entry, or a null entry, contributes nothing: an
   *                    unresolved product must never be counted into a
   *                    pool, because an over-count promises a discount the
   *                    checkout will not give.
   * @param rules       window.OF_BADGE_RULES (or any subset). Rules with no
   *                    usable tier ladder are skipped.
   *
   * Lanes come back in reading order: unfinished ladders first, deepest
   * progress first, then the finished ones. Finished ladders KEEP their
   * lane (P1-4). The achieved state is the one worth showing; the widget
   * used to delete the row at the exact moment the customer won.
   */
  function computeLanes(cartItems, tagsByHandle, rules) {
    var items = cartItems || [];
    var tagMap = tagsByHandle || {};
    var list = rules || [];
    var lanes = [];
    var anyGold = false;

    for (var r = 0; r < list.length; r++) {
      var rule = list[r];
      if (!rule) continue;
      if (!ascTiers(rule).length) continue;
      var qty = 0;
      for (var i = 0; i < items.length; i++) {
        var item = items[i] || {};
        var tags = tagMap[item.handle];
        if (!tags) continue;
        if (!matches(normalizeTags(tags), rule)) continue;
        qty += Number(item.quantity) || 0;
      }
      if (qty <= 0) continue;
      var lane = laneFor(rule, qty);
      if (!lane) continue;
      if (lane.complete) anyGold = true;
      lanes.push(lane);
    }

    lanes.sort(function (a, b) {
      if (a.complete !== b.complete) return a.complete ? 1 : -1;
      return b.pct - a.pct;
    });

    return { lanes: lanes, anyGold: anyGold, started: lanes.length > 0 };
  }

  /* What the header gauge paints. Gold outranks everything: one pool at
     its top tier crowns the whole icon. Otherwise the started lanes stripe
     their own colors and the fill level is the deepest lane's progress. */
  function gaugeState(result, limit) {
    var lanes = (result && result.lanes) || [];
    var cap = limit == null ? 3 : limit;
    var shown = lanes.slice(0, cap);
    return {
      gold: !!(result && result.anyGold),
      goldColor: GOLD,
      fillPct: shown.length ? shown[0].pct : 0,
      colors: shown.map(function (l) { return l.color; }),
      lanes: shown,
      n: lanes.length
    };
  }

  /* Counter-clerk copy. One place, so the header tooltip, the drawer lane
     and any future surface say the same thing the same way. */
  function laneStatus(lane) {
    if (!lane) return '';
    if (lane.complete) return 'Top tier · ' + lane.currentPercent + '% secured';
    if (lane.currentPercent > 0) return lane.currentPercent + '% secured';
    return 'Not started yet';
  }

  function laneLead(lane) {
    if (!lane) return '';
    if (lane.complete) return 'Deal secured. You reached the best price.';
    var n = lane.toNext;
    var unit = n === 1 ? 'one' : String(n);
    if (lane.currentPercent === 0) {
      return n === 1
        ? 'One more starts your ' + lane.nextPercent + '%'
        : 'Add ' + unit + ' more to start saving';
    }
    return n === 1
      ? 'One more unlocks ' + lane.nextPercent + '%'
      : 'Add ' + unit + ' more to hit ' + lane.nextPercent + '%';
  }

  /* The header label under the icon, and the drawer's section subhead. */
  function benchLabel(result) {
    var n = ((result && result.lanes) || []).length;
    if (!n) return '';
    return n + (n === 1 ? ' deal' : ' deals') + ' on your bench';
  }

  /* Ceremony diff: which lanes crossed a tier since the previous snapshot,
     and whether gold was just struck. Pure, so the sheen and the seal fire
     on the same rule in the header and in the drawer. */
  function crossings(prevLanes, nextLanes) {
    var was = {};
    (prevLanes || []).forEach(function (l) { if (l && l.slug) was[l.slug] = l; });
    var out = [];
    (nextLanes || []).forEach(function (l) {
      if (!l || !l.slug) return;
      var p = was[l.slug];
      if (p && l.tierIndex > p.tierIndex) out.push(l.slug);
    });
    return out;
  }

  return {
    GOLD: GOLD,
    norm: norm,
    normalizeTags: normalizeTags,
    matches: matches,
    safeColor: safeColor,
    LANE_PALETTE: LANE_PALETTE,
    LANE_COLORS: LANE_COLORS,
    laneColor: laneColor,
    ascTiers: ascTiers,
    railPct: railPct,
    tickPercents: tickPercents,
    labelPercents: labelPercents,
    laneFor: laneFor,
    computeLanes: computeLanes,
    gaugeState: gaugeState,
    laneStatus: laneStatus,
    laneLead: laneLead,
    benchLabel: benchLabel,
    crossings: crossings
  };
});
